/**
 * CrewSchedule — /schedule — THE schedule for the whole company (mobile-first).
 *
 * One surface replaces both the old admin Pipeline "Schedule" board and the
 * first-cut crew day list:
 *   • Day / Week / Month views (day = crew's field view, week = dispatch board,
 *     month = planning overview)
 *   • Filter by crew member, visits/tasks, show-done
 *   • Click anything → full detail (board shows titles only)
 *   • Dispatch (admin or edit_schedule): book visits, drag-or-dialog reschedule,
 *     reassign techs, create/edit/delete tasks — all pushed straight into Jobber
 *   • Crew keep everything from v1: notes + photos (offline-queued outbox),
 *     native clock in/out, "works when Jobber is down" reads
 *
 * Reads come from ElecBid's Supabase mirrors via /api/crew-schedule (my-day) —
 * they survive a Jobber outage. WRITES go live into Jobber (visitEditSchedule /
 * visitCreate / taskCreate…) and return a clear error while Jobber is down:
 * double-booking against an unreachable system of record is worse than waiting.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/apiFetch";
import { supabase } from "../lib/supabase";
import { usePermissions } from "../hooks/usePermissions";
import { canAccessPath } from "../config/permissions";

const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1e2328", border: "#2a3140",
  lime: "#a5ce3a", text: "#e8eaf0", muted: "#8892a4", faint: "#5a6374",
  red: "#e5484d", green: "#46a758", yellow: "#f5d90a", blue: "#3b82f6",
};
const PHOTO_BUCKET = "assessment-videos";
const PREFS_KEY = "elecbid.schedule.prefs";

const TECH_COLORS = ["#a5ce3a", "#3b82f6", "#f59e0b", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#22d3ee", "#e879f9", "#facc15"];
function techColor(name) {
  if (!name) return C.faint;
  let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return TECH_COLORS[h % TECH_COLORS.length];
}
function startOfWeek(d) {
  const x = new Date(d); const day = (x.getDay() + 6) % 7; // Monday = 0
  x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x;
}
function startOfMonthGrid(d) {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  return startOfWeek(first);
}
// DST-proof day stepping. NEVER use +n*864e5: across the fall-back Sunday that
// lands at 23:00 of the previous day, which rendered Nov 1 twice and shifted
// every later date one weekday column right. Y/M/D construction always yields
// local midnight of the intended day.
function addDays(d, n) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();
const dateInputVal = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
const timeInputVal = (d) => {
  const x = new Date(d);
  return `${String(x.getHours()).padStart(2, "0")}:${String(x.getMinutes()).padStart(2, "0")}`;
};

// Phone photos are 3–8 MB; shrink before upload so jobsite LTE doesn't choke.
async function downscalePhoto(file, maxDim = 1600, quality = 0.82) {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    if (scale >= 1 && file.size < 1_200_000) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    return blob || file;
  } catch {
    return file; // e.g. HEIC the browser can't decode — upload the original
  }
}

// Never rejects — a jobsite dead zone must surface as a friendly error, not an
// unhandled rejection that hangs the screen or wedges a button.
async function api(action, body = {}) {
  let r;
  try {
    r = await apiFetch("/api/crew-schedule", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, ...body }),
    });
  } catch {
    return { ok: false, error: "No connection — check your signal and try again" };
  }
  return r.json().catch(() => ({ ok: false, error: `HTTP ${r.status}` }));
}

// Which jobs deserve the "⚡ Generator report" button (generator unit on file OR
// generator-shaped job title). Asked once per window-load, best-effort: a
// failure here must never keep the schedule itself from rendering.
async function genApplicable(jobNumbers) {
  if (!jobNumbers.length) return {};
  try {
    const r = await apiFetch("/api/generator-maintenance", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "applicable", jobNumbers }),
    });
    const j = await r.json();
    return j.ok ? j.byJob || {} : {};
  } catch {
    return {};
  }
}

function Btn({ children, kind, disabled, onClick, style, title }) {
  const base = {
    padding: "9px 14px", borderRadius: 8, fontSize: 13.5, fontWeight: 700,
    border: `1px solid ${kind === "pri" ? C.lime : kind === "danger" ? C.red : C.border}`,
    background: kind === "pri" ? C.lime : kind === "danger" ? "rgba(229,72,77,.12)" : C.card,
    color: kind === "pri" ? "#10130a" : kind === "danger" ? C.red : C.text,
    cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1,
  };
  return <button title={title} onClick={onClick} disabled={disabled} style={{ ...base, ...style }}>{children}</button>;
}

function Pill({ active, onClick, children, color }) {
  return (
    <button onClick={onClick} style={{
      padding: "6px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
      border: `1px solid ${active ? (color || C.lime) : C.border}`,
      background: active ? "rgba(165,206,58,.14)" : C.card,
      color: active ? (color || C.lime) : C.muted,
      whiteSpace: "nowrap",
    }}>{children}</button>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(6,8,10,.72)", zIndex: 300,
      display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 0,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: "14px 14px 0 0",
        width: "100%", maxWidth: wide ? 860 : 620, maxHeight: "88vh", overflowY: "auto",
        padding: "16px 16px 24px", boxSizing: "border-box",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 21, letterSpacing: ".04em", color: C.text }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, fontSize: 24, cursor: "pointer", lineHeight: 1, padding: 4 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    pending: { label: "Queued — posts to Jobber automatically", color: C.yellow },
    sending: { label: "Posting to Jobber…", color: C.blue },
    synced: { label: "In Jobber ✓", color: C.green },
    failed: { label: "Couldn't post — tell the office", color: C.red },
  };
  const m = map[status] || map.pending;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: m.color, border: `1px solid ${m.color}`, borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" }}>
      {m.label}
    </span>
  );
}

const inputStyle = {
  background: C.card, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8,
  padding: "9px 10px", fontSize: 14, boxSizing: "border-box", width: "100%",
  colorScheme: "dark",
};
const labelStyle = { fontSize: 11.5, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 4 };

// ── Crew multi-select (assignment picker) ───────────────────────────────────
function CrewPicker({ crew, value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {crew.map((u) => {
        const on = value.includes(u.id);
        return (
          <Pill key={u.id} active={on} color={techColor(u.name)}
            onClick={() => onChange(on ? value.filter((x) => x !== u.id) : [...value, u.id])}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: techColor(u.name) }} />
              {u.name.split(" ")[0]}
            </span>
          </Pill>
        );
      })}
    </div>
  );
}

// ── Note composer (unchanged from v1, lives in the visit detail) ────────────
function NoteComposer({ visit, clientName, jobberJobId, onQueued }) {
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);

  const addFiles = (files) => {
    const next = [...photos];
    for (const f of Array.from(files || [])) {
      if (next.length >= 20) break;
      next.push({ id: crypto.randomUUID(), file: f, previewUrl: URL.createObjectURL(f) });
    }
    setPhotos(next);
  };

  const submit = async () => {
    if (!text.trim() && !photos.length) return;
    setBusy(true); setErr(null);
    try {
      const paths = [];
      for (const p of photos) {
        const blob = await downscalePhoto(p.file);
        const path = `crew-notes/${visit.jobNumber || "general"}/${p.id}.jpg`;
        const { error } = await supabase.storage.from(PHOTO_BUCKET).upload(path, blob, { contentType: "image/jpeg", upsert: true });
        if (error) throw new Error(`Photo upload failed — ${error.message || "check your signal and try again"}`);
        paths.push(path);
      }
      const r = await api("add-note", {
        jobNumber: visit.jobNumber,
        jobberJobId: jobberJobId || null,
        visitId: visit.id,
        visitTitle: visit.title || visit.jobTitle || null,
        message: text.trim(),
        photoPaths: paths,
      });
      if (!r.ok) throw new Error(r.error || "Couldn't save the note");
      photos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      setText(""); setPhotos([]);
      if (r.duplicate) setErr("Looks identical to a note you just posted — not sent twice.");
      onQueued && onQueued(r.note);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`Add a note for ${clientName || (visit.jobNumber ? `job #${visit.jobNumber}` : "this job")} — what got done, what's left, materials used…`}
        rows={3}
        style={{ ...inputStyle, resize: "vertical", background: C.bg }}
      />
      {photos.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {photos.map((p) => (
            <div key={p.id} style={{ position: "relative" }}>
              <img src={p.previewUrl} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: `1px solid ${C.border}` }} />
              <button
                onClick={() => setPhotos(photos.filter((x) => x.id !== p.id))}
                style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: 999, border: "none", background: C.red, color: "#fff", fontSize: 11, fontWeight: 800, cursor: "pointer", lineHeight: "20px", padding: 0 }}
              >×</button>
            </div>
          ))}
        </div>
      )}
      {err && <div style={{ color: C.red, fontSize: 12.5, marginTop: 8 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <Btn onClick={() => fileRef.current?.click()} disabled={busy}>📷 Photos{photos.length ? ` (${photos.length})` : ""}</Btn>
        <Btn kind="pri" onClick={submit} disabled={busy || (!text.trim() && !photos.length)} style={{ flex: 1 }}>
          {busy ? "Saving…" : "Save note"}
        </Btn>
      </div>
      <div style={{ fontSize: 11.5, color: C.faint, marginTop: 6 }}>
        Saves instantly to ElecBid, posts to the Jobber job automatically — even if Jobber is down right now.
      </div>
    </div>
  );
}

// ── Task create/edit dialog ─────────────────────────────────────────────────
function TaskDialog({ crew, initial, onSave, onClose }) {
  const isEdit = !!initial?.id;
  const [title, setTitle] = useState(initial?.title || "");
  const [instructions, setInstructions] = useState(initial?.instructions || "");
  // initial.date (date-only, e.g. "+ New task" from the board) keeps the 08:00
  // start default; initial.startAt (editing a real task) carries its real time.
  const [date, setDate] = useState(initial?.date || (initial?.startAt ? dateInputVal(initial.startAt) : dateInputVal(new Date())));
  const [time, setTime] = useState(initial?.startAt && !initial?.allDay ? timeInputVal(initial.startAt) : "08:00");
  const [endTime, setEndTime] = useState(initial?.endAt && !initial?.allDay ? timeInputVal(initial.endAt) : "");
  const [allDay, setAllDay] = useState(!!initial?.allDay);
  const [assigned, setAssigned] = useState(initial?.assignedIds || []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    // Validate BEFORE setBusy — a cleared native date/time input would make
    // toISOString throw and wedge the button at "Saving…" forever.
    if (!title.trim()) { setErr("Give it a title"); return; }
    const start = new Date(`${date}T${allDay ? "00:00" : time}`);
    if (!date || (!allDay && !time) || isNaN(start)) { setErr("Pick a date and time"); return; }
    const end = !allDay && endTime ? new Date(`${date}T${endTime}`) : null;
    if (end && isNaN(end)) { setErr("End time is invalid"); return; }
    setBusy(true); setErr(null);
    try {
      const r = await onSave({
        ...(isEdit ? { taskId: initial.id } : {}),
        title: title.trim(), instructions: instructions.trim(),
        startAt: start.toISOString(), endAt: end ? end.toISOString() : null,
        allDay, assignedUserIds: assigned,
      });
      if (!r.ok) setErr(r.error || "Couldn't save"); else onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={isEdit ? "Edit task" : "New task"} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <span style={labelStyle}>Title (what shows on the schedule)</span>
          <input style={inputStyle} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Pick up panel from EBH" />
        </div>
        <div>
          <span style={labelStyle}>Details</span>
          <textarea style={{ ...inputStyle, resize: "vertical" }} rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Anything the person needs to know (only shown when the task is opened)" />
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 130px" }}>
            <span style={labelStyle}>Date</span>
            <input type="date" style={inputStyle} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          {!allDay && (
            <div style={{ flex: "1 1 100px" }}>
              <span style={labelStyle}>Start</span>
              <input type="time" style={inputStyle} value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          )}
          {!allDay && (
            <div style={{ flex: "1 1 100px" }}>
              <span style={labelStyle}>End (optional)</span>
              <input type="time" style={inputStyle} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.text, alignSelf: "flex-end", paddingBottom: 9, cursor: "pointer" }}>
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} style={{ accentColor: C.lime }} />
            All day
          </label>
        </div>
        <div>
          <span style={labelStyle}>Assign to</span>
          <CrewPicker crew={crew} value={assigned} onChange={setAssigned} />
        </div>
        {err && <div style={{ color: C.red, fontSize: 13 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn kind="pri" onClick={save} disabled={busy}>{busy ? "Saving…" : isEdit ? "Save changes" : "Create task"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── Book-a-visit dialog (job search + schedule + techs) ─────────────────────
function BookVisitDialog({ crew, defaultDate, onSave, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [job, setJob] = useState(null);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(dateInputVal(defaultDate || new Date()));
  const [time, setTime] = useState("08:00");
  const [endTime, setEndTime] = useState("16:00");
  const [assigned, setAssigned] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      const r = await api("job-search", { q });
      if (r.ok) setResults(r.jobs || []);
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [q]);

  const save = async () => {
    if (!job) { setErr("Pick a job first"); return; }
    const start = new Date(`${date}T${time}`);
    if (!date || !time || isNaN(start)) { setErr("Pick a date and start time"); return; }
    const end = endTime ? new Date(`${date}T${endTime}`) : null;
    if (end && isNaN(end)) { setErr("End time is invalid"); return; }
    // "archived" is Jobber's NORMAL terminal state — booking onto one is legal
    // but usually a mistake, so confirm rather than block.
    if (/archiv|closed|complete|cancel/i.test(job.status || "") &&
        !window.confirm(`Job #${job.jobNumber} looks ${job.status} — book a visit on it anyway?`)) return;
    setBusy(true); setErr(null);
    try {
      const r = await onSave({
        jobNumber: job.jobNumber,
        title: title.trim() || null,
        startAt: start.toISOString(),
        endAt: end ? end.toISOString() : null,
        assignedUserIds: assigned,
      });
      if (!r.ok) setErr(r.error || "Couldn't book"); else onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Book a visit" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <span style={labelStyle}>Job</span>
          {job ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.card, border: `1px solid ${C.lime}`, borderRadius: 8, padding: "9px 10px" }}>
              <div style={{ flex: 1, fontSize: 13.5 }}>
                <b>#{job.jobNumber}</b> — {job.title || "Job"} <span style={{ color: C.muted }}>· {job.clientName}</span>
              </div>
              <button onClick={() => setJob(null)} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 16 }}>×</button>
            </div>
          ) : (
            <>
              <input style={inputStyle} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by job #, title or client…" autoFocus />
              {results.length > 0 && (
                <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4, maxHeight: 180, overflowY: "auto" }}>
                  {results.map((j) => (
                    <button key={j.jobNumber} onClick={() => { setJob(j); setResults([]); setQ(""); }}
                      style={{ textAlign: "left", background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px", cursor: "pointer", color: C.text, fontSize: 13 }}>
                      <b>#{j.jobNumber}</b> — {j.title || "Job"} <span style={{ color: C.muted }}>· {j.clientName}{j.status ? ` · ${j.status}` : ""}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div>
          <span style={labelStyle}>Visit title (optional — defaults to the job)</span>
          <input style={inputStyle} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Rough-in day 2" />
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 130px" }}>
            <span style={labelStyle}>Date</span>
            <input type="date" style={inputStyle} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div style={{ flex: "1 1 100px" }}>
            <span style={labelStyle}>Start</span>
            <input type="time" style={inputStyle} value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div style={{ flex: "1 1 100px" }}>
            <span style={labelStyle}>End</span>
            <input type="time" style={inputStyle} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
        </div>
        <div>
          <span style={labelStyle}>Assign techs</span>
          <CrewPicker crew={crew} value={assigned} onChange={setAssigned} />
        </div>
        {err && <div style={{ color: C.red, fontSize: 13 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn kind="pri" onClick={save} disabled={busy}>{busy ? "Booking…" : "Book visit — updates Jobber"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── Reschedule dialog (visit move) ──────────────────────────────────────────
function RescheduleDialog({ item, onSave, onClose }) {
  const [date, setDate] = useState(dateInputVal(item.startAt));
  const [time, setTime] = useState(timeInputVal(item.startAt));
  const [endTime, setEndTime] = useState(item.endAt ? timeInputVal(item.endAt) : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    const start = new Date(`${date}T${time}`);
    if (!date || !time || isNaN(start)) { setErr("Pick a date and start time"); return; }
    // A visit that HAS an end must keep one — clearing it would leave Jobber's
    // old end time behind the new start (end-before-start).
    if (item.endAt && !endTime) { setErr("This one has an end time — set the new one too"); return; }
    const end = endTime ? new Date(`${date}T${endTime}`) : null;
    if (end && isNaN(end)) { setErr("End time is invalid"); return; }
    setBusy(true); setErr(null);
    try {
      const r = await onSave(start.toISOString(), end ? end.toISOString() : null);
      if (!r.ok) setErr(r.error || "Couldn't reschedule"); else onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Reschedule ${item.jobNumber ? `#${item.jobNumber}` : ""}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 130px" }}>
            <span style={labelStyle}>Date</span>
            <input type="date" style={inputStyle} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div style={{ flex: "1 1 100px" }}>
            <span style={labelStyle}>Start</span>
            <input type="time" style={inputStyle} value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div style={{ flex: "1 1 100px" }}>
            <span style={labelStyle}>End</span>
            <input type="time" style={inputStyle} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
        </div>
        {err && <div style={{ color: C.red, fontSize: 13 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn kind="pri" onClick={save} disabled={busy}>{busy ? "Moving…" : "Reschedule — updates Jobber"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function CrewSchedule() {
  const perms = usePermissions();
  const [view, setView] = useState(() => {
    try { const p = JSON.parse(localStorage.getItem(PREFS_KEY) || "{}"); if (p.view) return p.view; } catch {}
    return window.innerWidth < 768 ? "day" : "week";
  });
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [techFilter, setTechFilter] = useState(null); // null = everyone; Set of names
  const [typeFilter, setTypeFilter] = useState({ visits: true, tasks: true });
  const [showDone, setShowDone] = useState(true);
  const [detail, setDetail] = useState(null);      // { kind: 'visit'|'task', item }
  const [dialog, setDialog] = useState(null);      // { type: 'task-new'|'task-edit'|'visit-new'|'reschedule', ... }
  const [newMenu, setNewMenu] = useState(false);
  const [notesByJob, setNotesByJob] = useState({});
  const [clockBusy, setClockBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState(null);
  const [drag, setDrag] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [moveConfirm, setMoveConfirm] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [genByJob, setGenByJob] = useState({});

  // Fetch window per view (one my-day call covers the whole visible range).
  // Keyed on PRIMITIVES on purpose: anchor is a fresh Date on every day-strip
  // tap, but as long as it stays inside the same week/month grid the window is
  // numerically identical — no refetch, no loading flash, and (critically) no
  // wiping the schedule a tech is reading in a dead zone.
  const rangeStartMs = useMemo(
    () => (view === "month" ? startOfMonthGrid(anchor) : startOfWeek(anchor)).getTime(),
    [view, anchor]
  );
  const rangeDays = view === "month" ? 42 : 7;

  const load = useCallback(async () => {
    setErr(null);
    const r = await api("my-day", { start: new Date(rangeStartMs).toISOString(), days: rangeDays });
    if (!r.ok) { setErr(r.error || "Couldn't load the schedule"); return; }
    setData(r);
    const jobNums = [...new Set((r.visits || []).map((v) => v.jobNumber).filter(Boolean))];
    setGenByJob(await genApplicable(jobNums));
  }, [rangeStartMs, rangeDays]);
  useEffect(() => { setData(null); load(); }, [load]);

  // Persist view preference.
  useEffect(() => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify({ view })); } catch {}
  }, [view]);

  useEffect(() => {
    if (!data?.clock) return;
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, [data?.clock]);

  const loadNotes = useCallback(async (jobNumber) => {
    const r = await api("my-notes", { jobNumber, limit: 20 });
    if (r.ok) setNotesByJob((m) => ({ ...m, [jobNumber]: r.notes }));
  }, []);

  const canEdit = !!data?.canEdit;
  const crew = data?.crew || [];
  const jobsMeta = data?.jobs || {};
  const identity = data?.identity;
  const clock = data?.clock || null;
  const clockElapsed = clock ? Math.max(0, (nowTick - new Date(clock.clock_in_at).getTime()) / 3600000) : 0;
  const canOpenJob = canAccessPath("/job/:jobNum", { role: perms.role, permissions: perms.permissions, isSuperadmin: perms.isSuperadmin, realIsOwner: perms.realIsOwner });

  // ── Filtering ─────────────────────────────────────────────────────────────
  const matchesTech = (item) => {
    if (!techFilter || !techFilter.size) return true;
    if (!item.assigned.length) return techFilter.has("__unassigned");
    return item.assigned.some((n) => techFilter.has(n));
  };
  const visits = (data?.visits || []).filter((v) => typeFilter.visits && matchesTech(v) && (showDone || !v.complete));
  const tasks = (data?.tasks || []).filter((t) => typeFilter.tasks && matchesTech(t) && (showDone || !t.complete));
  const allTechNames = useMemo(() => {
    const s = new Set();
    (data?.visits || []).forEach((v) => v.assigned.forEach((n) => s.add(n)));
    (data?.tasks || []).forEach((t) => t.assigned.forEach((n) => s.add(n)));
    crew.forEach((u) => s.add(u.name));
    return [...s].sort();
  }, [data, crew]);

  // Bucketed once per data/filter change — the month grid asks for 42 days and
  // the clock ticker re-renders every 30s, so per-call full scans would jank
  // low-end phones.
  const dayBuckets = useMemo(() => {
    const m = new Map();
    const push = (kind, item) => {
      if (!item.startAt) return;
      const k = new Date(item.startAt).toDateString();
      if (!m.has(k)) m.set(k, []);
      m.get(k).push({ kind, item });
    };
    visits.forEach((v) => push("visit", v));
    tasks.forEach((t) => push("task", t));
    for (const list of m.values()) list.sort((a, b) => new Date(a.item.startAt) - new Date(b.item.startAt));
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, techFilter, typeFilter, showDone]);
  const itemsForDay = (d) => dayBuckets.get(new Date(d).toDateString()) || [];

  // ── Actions ───────────────────────────────────────────────────────────────
  const doClockIn = async (visit) => {
    setClockBusy(true);
    try {
      let r = await api("clock-in", visit ? { jobNumber: visit.jobNumber, jobTitle: visit.jobTitle || visit.title, visitId: visit.id } : {});
      if (!r.ok && r.error === "already_clocked_in" && r.open) {
        const from = r.open.job_number ? `job #${r.open.job_number}` : "your current entry";
        if (window.confirm(`You're already clocked in on ${from}. Clock out of it and start ${visit?.jobNumber ? `job #${visit.jobNumber}` : "the new entry"}?`)) {
          r = await api("clock-in", { ...(visit ? { jobNumber: visit.jobNumber, jobTitle: visit.jobTitle || visit.title, visitId: visit.id } : {}), force: true });
        }
      }
      if (r.ok) load(); else if (r.error && r.error !== "already_clocked_in") alert(r.hint || r.error);
    } finally {
      setClockBusy(false);
    }
  };
  const doClockOut = async () => {
    const note = window.prompt("Anything to note for these hours? (optional)") || "";
    setClockBusy(true);
    try {
      const r = await api("clock-out", { note });
      if (r.ok) {
        if (r.needsReview) alert("That entry ran over 14 hours, so it was held for office review instead of going to billing.");
        load();
      } else alert(r.hint || r.error || "Clock-out failed");
    } finally {
      setClockBusy(false);
    }
  };

  const doMove = async (kind, item, newStart, newEnd) => {
    setBusyMsg("Updating Jobber…");
    let r;
    if (kind === "visit") {
      r = await api("visit-move", { visitId: item.id, startAt: newStart, endAt: newEnd || undefined });
    } else {
      r = await api("task-edit", {
        taskId: item.id, title: item.title, instructions: item.instructions,
        startAt: newStart, endAt: newEnd, allDay: item.allDay, assignedUserIds: item.assignedIds || [],
      });
    }
    setBusyMsg(null);
    if (r.ok) { setDetail(null); load(); }
    return r;
  };

  // Drag-drop (week view, editors): drop onto a day keeps the original wall time.
  const onDropDay = (day) => {
    setDragOver(null);
    const d = drag; setDrag(null);
    if (!d) return;
    const orig = new Date(d.item.startAt);
    const ns = new Date(day); ns.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
    if (ns.toDateString() === orig.toDateString()) return;
    const dur = d.item.endAt ? (new Date(d.item.endAt).getTime() - orig.getTime()) : 0;
    const ne = dur > 0 ? new Date(ns.getTime() + dur) : null;
    setMoveConfirm({
      ...d, newStart: ns, newEnd: ne,
      dayLabel: day.toLocaleDateString("en-CA", { weekday: "long", month: "short", day: "numeric" }),
    });
  };

  // ── Range labels + nav ────────────────────────────────────────────────────
  const nav = (dir) => {
    const d = new Date(anchor);
    if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + 7 * dir);
    else { d.setDate(1); d.setMonth(d.getMonth() + dir); } // day-31 anchors would skip/stall months otherwise
    setAnchor(d);
  };
  const rangeLabel = view === "day"
    ? anchor.toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" })
    : view === "week"
      ? (() => { const s = startOfWeek(anchor); const e = addDays(s, 6); return `${s.toLocaleDateString("en-CA", { month: "short", day: "numeric" })} – ${e.toLocaleDateString("en-CA", { month: "short", day: "numeric" })}`; })()
      : anchor.toLocaleDateString("en-CA", { month: "long", year: "numeric" });
  const isToday = (d) => d.toDateString() === new Date().toDateString();

  // ── Shared item block (week/month) ────────────────────────────────────────
  const ItemBlock = ({ kind, item, compact }) => {
    const color = techColor(item.assigned[0]);
    const isTask = kind === "task";
    return (
      <div
        draggable={canEdit && !item.complete}
        onDragStart={() => canEdit && setDrag({ kind, item })}
        onDragEnd={() => { setDrag(null); setDragOver(null); }}
        onClick={() => { setDetail({ kind, item }); if (kind === "visit" && item.jobNumber && !notesByJob[item.jobNumber]) loadNotes(item.jobNumber); }}
        title={`${item.title || item.jobTitle || (isTask ? "Task" : "Visit")}${item.assigned.length ? " · " + item.assigned.join(", ") : ""}`}
        style={{
          background: isTask ? "transparent" : C.card,
          border: isTask ? `1px dashed ${color}` : "none",
          borderLeft: isTask ? `1px dashed ${color}` : `4px solid ${color}`,
          borderRadius: 7, padding: compact ? "3px 6px" : "6px 8px",
          cursor: "pointer", opacity: item.complete ? 0.5 : 1,
        }}>
        <div style={{ fontSize: compact ? 9.5 : 11, color: isTask ? C.muted : C.lime, fontWeight: 700 }}>
          {isTask ? "📋 " : ""}{item.allDay ? "All day" : fmtTime(item.startAt)}
        </div>
        <div style={{
          fontSize: compact ? 10.5 : 12, fontWeight: 600, color: C.text, lineHeight: 1.25,
          overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: compact ? 1 : 2, WebkitBoxOrient: "vertical",
        }}>
          {kind === "visit" && item.jobNumber ? `#${item.jobNumber} ` : ""}{item.title || item.jobTitle || (isTask ? "Task" : "Visit")}
        </div>
        {!compact && item.assigned.length > 0 && (
          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {item.assigned.map((n) => n.split(" ")[0]).join(", ")}
          </div>
        )}
      </div>
    );
  };

  // ═══ Render ═══════════════════════════════════════════════════════════════
  return (
    <div style={{ maxWidth: view === "day" ? 760 : 1280, margin: "0 auto", padding: "14px 12px 90px", color: C.text }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, letterSpacing: ".04em", margin: 0 }}>Schedule</h1>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {["day", "week", "month"].map((v) => (
            <Pill key={v} active={view === v} onClick={() => setView(v)}>{v[0].toUpperCase() + v.slice(1)}</Pill>
          ))}
          {canEdit && (
            <div style={{ position: "relative" }}>
              <Btn kind="pri" onClick={() => setNewMenu(!newMenu)} style={{ padding: "7px 14px" }}>＋ New</Btn>
              {newMenu && (
                <div style={{ position: "absolute", right: 0, top: "110%", zIndex: 200, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", minWidth: 170, boxShadow: "0 8px 24px rgba(0,0,0,.5)" }}>
                  {[["visit-new", "📅 Book a visit"], ["task-new", "📋 New task"]].map(([t, label]) => (
                    <button key={t} onClick={() => { setNewMenu(false); setDialog({ type: t }); }}
                      style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", color: C.text, fontSize: 13.5, padding: "10px 14px", cursor: "pointer" }}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Nav row */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
        <Btn onClick={() => nav(-1)} style={{ padding: "6px 10px" }}>‹</Btn>
        <Btn onClick={() => { const t = new Date(); t.setHours(0, 0, 0, 0); setAnchor(t); }} style={{ padding: "6px 10px" }}>Today</Btn>
        <Btn onClick={() => nav(1)} style={{ padding: "6px 10px" }}>›</Btn>
        <span style={{ fontSize: 14.5, fontWeight: 700, marginLeft: 6 }}>{rangeLabel}</span>
      </div>

      {/* Filter row */}
      <div style={{ display: "flex", gap: 6, alignItems: "center", overflowX: "auto", paddingBottom: 6, marginBottom: 10 }}>
        <Pill active={!techFilter} onClick={() => setTechFilter(null)}>Everyone</Pill>
        {identity?.mapped && (
          <Pill active={!!techFilter && techFilter.has(identity.jobberUserName)} color={techColor(identity.jobberUserName)}
            onClick={() => setTechFilter(new Set([identity.jobberUserName]))}>Me</Pill>
        )}
        {allTechNames.map((n) => (
          <Pill key={n} active={!!techFilter && techFilter.has(n)} color={techColor(n)}
            onClick={() => {
              const next = new Set(techFilter || []);
              if (next.has(n)) next.delete(n); else next.add(n);
              setTechFilter(next.size ? next : null);
            }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: techColor(n) }} />
              {n.split(" ")[0]}
            </span>
          </Pill>
        ))}
        <span style={{ width: 1, alignSelf: "stretch", background: C.border, margin: "0 4px", flexShrink: 0 }} />
        <Pill active={typeFilter.visits} onClick={() => setTypeFilter({ ...typeFilter, visits: !typeFilter.visits })}>Visits</Pill>
        <Pill active={typeFilter.tasks} onClick={() => setTypeFilter({ ...typeFilter, tasks: !typeFilter.tasks })}>Tasks</Pill>
        <Pill active={showDone} onClick={() => setShowDone(!showDone)}>Done ✓</Pill>
      </div>

      {/* Unmapped / ambiguous banner */}
      {identity && !identity.mapped && (
        <div style={{ background: "rgba(245,217,10,.08)", border: `1px solid ${C.yellow}`, borderRadius: 10, padding: "10px 12px", fontSize: 13, marginBottom: 10 }}>
          {identity.ambiguous
            ? "More than one Jobber tech matches your first name — ask Dylan to set your FULL Jobber name in Manage Crew so hours land on the right person."
            : "Your login isn't linked to a Jobber tech yet — ask Dylan to set your Jobber name in Manage Crew. Clock-in still works meanwhile."}
        </div>
      )}

      {/* Clock bar — ALWAYS visible in every view (the view pref persists, so a
          week/month user must never lose their clock-in). */}
      {clock ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(70,167,88,.10)", border: `1px solid ${C.green}`, borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
          <span style={{ width: 10, height: 10, borderRadius: 999, background: C.green, boxShadow: `0 0 8px ${C.green}` }} />
          <div style={{ flex: 1, fontSize: 13.5 }}>
            <b>Clocked in</b>{clock.job_number ? <> on <b>#{clock.job_number}</b>{clock.job_title ? ` — ${clock.job_title}` : ""}</> : " (no job)"} since {fmtTime(clock.clock_in_at)}
            <span style={{ color: C.green, fontWeight: 800 }}> · {clockElapsed.toFixed(1)}h</span>
          </div>
          <Btn kind="danger" onClick={doClockOut} disabled={clockBusy}>{clockBusy ? "…" : "Clock out"}</Btn>
        </div>
      ) : data ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 12px", marginBottom: 10 }}>
          <span style={{ flex: 1, fontSize: 13, color: C.muted }}>Not clocked in — open a visit to clock in on its job, or</span>
          <Btn onClick={() => doClockIn(null)} disabled={clockBusy} style={{ padding: "7px 12px" }}>⏱ Clock in (no job)</Btn>
        </div>
      ) : null}

      {err && (
        <div style={{ background: "rgba(229,72,77,.08)", border: `1px solid ${C.red}`, borderRadius: 10, padding: 12, color: C.red, fontSize: 13.5, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ flex: 1 }}>Couldn't load: {err}</span>
          <Btn onClick={() => { setData(null); load(); }}>Retry</Btn>
        </div>
      )}
      {!data && !err && <div style={{ color: C.muted, textAlign: "center", padding: 30, fontSize: 14 }}>Loading the schedule…</div>}

      {/* ══ DAY VIEW ══ */}
      {data && view === "day" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* day strip within the fetched week */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
            {Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i)).map((d) => {
              const sel = d.toDateString() === anchor.toDateString();
              const n = itemsForDay(d).length;
              return (
                <button key={d.toISOString()} onClick={() => setAnchor(d)}
                  style={{
                    padding: "8px 2px", borderRadius: 10, cursor: "pointer", textAlign: "center",
                    border: `1px solid ${sel ? C.lime : C.border}`,
                    background: sel ? "rgba(165,206,58,.14)" : C.surface,
                  }}>
                  <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em" }}>{d.toLocaleDateString("en-CA", { weekday: "short" })}</div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: isToday(d) ? C.lime : C.text }}>{d.getDate()}</div>
                  <div style={{ fontSize: 10, color: n ? C.lime : C.faint, fontWeight: 700 }}>{n || "·"}</div>
                </button>
              );
            })}
          </div>

          {itemsForDay(anchor).map(({ kind, item }) => {
            const meta = jobsMeta[item.jobNumber] || {};
            const color = techColor(item.assigned[0]);
            const clockedHere = clock && kind === "visit" && clock.visit_id === item.id;
            return (
              <div key={item.id}
                onClick={() => { setDetail({ kind, item }); if (kind === "visit" && item.jobNumber && !notesByJob[item.jobNumber]) loadNotes(item.jobNumber); }}
                style={{
                  background: kind === "task" ? "transparent" : C.surface,
                  border: kind === "task" ? `1px dashed ${color}` : `1px solid ${C.border}`,
                  borderLeft: kind === "task" ? `1px dashed ${color}` : `5px solid ${color}`,
                  borderRadius: 12, padding: "12px 14px", cursor: "pointer", opacity: item.complete ? 0.55 : 1,
                }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                  <span style={{ color: kind === "task" ? C.muted : C.lime, fontWeight: 800, fontSize: 13.5 }}>
                    {kind === "task" ? "📋 " : ""}{item.allDay ? "All day" : `${fmtTime(item.startAt)}${item.endAt ? ` – ${fmtTime(item.endAt)}` : ""}`}
                  </span>
                  <span style={{ display: "flex", gap: 6 }}>
                    {item.mine && <span style={{ fontSize: 10.5, fontWeight: 800, color: C.lime }}>YOU</span>}
                    {item.complete && <span style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>Done ✓</span>}
                    {clockedHere && <span style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>⏱ on the clock</span>}
                  </span>
                </div>
                <div style={{ fontSize: 15.5, fontWeight: 700, marginTop: 3, lineHeight: 1.3 }}>
                  {kind === "visit" && item.jobNumber ? `#${item.jobNumber} — ` : ""}{item.title || item.jobTitle || (kind === "task" ? "Task" : "Visit")}
                </div>
                <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3 }}>
                  {(kind === "visit" ? meta.clientName : item.clientName) ? `${kind === "visit" ? meta.clientName : item.clientName} · ` : ""}
                  {item.assigned.join(", ") || "Unassigned"}
                </div>
              </div>
            );
          })}
          {!itemsForDay(anchor).length && (
            <div style={{ color: C.faint, textAlign: "center", padding: "28px 0", fontSize: 14 }}>Nothing scheduled this day.</div>
          )}
        </div>
      )}

      {/* ══ WEEK VIEW ══ */}
      {data && view === "week" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(140px, 1fr))", gap: 8, overflowX: "auto" }}>
          {Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i)).map((day) => {
            const list = itemsForDay(day);
            const over = dragOver === day.toDateString();
            return (
              <div key={day.toISOString()}
                onDragOver={(e) => { if (canEdit) { e.preventDefault(); setDragOver(day.toDateString()); } }}
                onDragLeave={() => setDragOver((d) => (d === day.toDateString() ? null : d))}
                onDrop={() => canEdit && onDropDay(day)}
                style={{
                  background: over ? "rgba(165,206,58,.10)" : C.surface,
                  border: `1px solid ${over ? C.lime : C.border}`, borderRadius: 10, padding: 8, minHeight: 300,
                }}>
                <button onClick={() => { setAnchor(day); setView("day"); }}
                  style={{ display: "block", width: "100%", background: "none", border: "none", cursor: "pointer", textAlign: "center", marginBottom: 8, paddingBottom: 6, borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 10.5, color: C.muted, textTransform: "uppercase", letterSpacing: ".06em" }}>{day.toLocaleDateString("en-CA", { weekday: "short" })}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: isToday(day) ? C.lime : C.text }}>{day.getDate()}</div>
                </button>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {list.map(({ kind, item }) => <ItemBlock key={item.id} kind={kind} item={item} />)}
                  {!list.length && <div style={{ color: "#3a4250", fontSize: 11, textAlign: "center", paddingTop: 14 }}>—</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {data && view === "week" && canEdit && (
        <div style={{ fontSize: 12, color: C.faint, marginTop: 10 }}>Drag a visit or task to another day to reschedule — you'll confirm before Jobber is updated. Click anything for details.</div>
      )}

      {/* ══ MONTH VIEW ══ */}
      {data && view === "month" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} style={{ textAlign: "center", fontSize: 10.5, color: C.muted, textTransform: "uppercase", letterSpacing: ".06em", padding: "4px 0" }}>{d}</div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
            {Array.from({ length: 42 }, (_, i) => addDays(startOfMonthGrid(anchor), i)).map((day) => {
              const list = itemsForDay(day);
              const inMonth = day.getMonth() === anchor.getMonth();
              return (
                <button key={day.toISOString()} onClick={() => { setAnchor(day); setView("day"); }}
                  style={{
                    background: C.surface, border: `1px solid ${isToday(day) ? C.lime : C.border}`,
                    borderRadius: 8, padding: "4px 4px 6px", minHeight: 74, cursor: "pointer",
                    opacity: inMonth ? 1 : 0.4, textAlign: "left", display: "flex", flexDirection: "column", gap: 3,
                  }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: isToday(day) ? C.lime : C.text, textAlign: "right", paddingRight: 2 }}>{day.getDate()}</div>
                  {list.slice(0, 3).map(({ kind, item }) => (
                    <div key={item.id} style={{
                      fontSize: 9.5, fontWeight: 600, color: C.text, lineHeight: 1.3,
                      borderLeft: `3px solid ${techColor(item.assigned[0])}`,
                      borderStyle: kind === "task" ? "none none none dashed" : "none none none solid",
                      paddingLeft: 4, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
                      opacity: item.complete ? 0.5 : 1,
                    }}>
                      {kind === "visit" && item.jobNumber ? `#${item.jobNumber} ` : ""}{item.title || item.jobTitle || "…"}
                    </div>
                  ))}
                  {list.length > 3 && <div style={{ fontSize: 9.5, color: C.muted, paddingLeft: 4 }}>+{list.length - 3} more</div>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Truncation warning — the server caps rows; a silently short month view
          would read as free days and invite double-booking. */}
      {data?.truncated && (
        <div style={{ background: "rgba(245,217,10,.08)", border: `1px solid ${C.yellow}`, borderRadius: 10, padding: "8px 12px", fontSize: 12.5, marginTop: 12 }}>
          Heads up: this window has more entries than the view loaded — the later days may be incomplete. Use Week view for the full picture.
        </div>
      )}

      {/* Footer */}
      {data && (
        <div style={{ fontSize: 11.5, color: C.faint, marginTop: 14, lineHeight: 1.5 }}>
          Schedule served from ElecBid's own mirror — viewing keeps working when Jobber is down.
          {canEdit && " Booking and rescheduling update Jobber directly, so they need Jobber up."}
          {" "}Notes &amp; photos queue and post automatically. Hours land in Crew Performance either way.
        </div>
      )}

      {/* ══ DETAIL MODAL ══ */}
      {detail && detail.kind === "visit" && (() => {
        const v = detail.item;
        const meta = jobsMeta[v.jobNumber] || {};
        const clockedHere = clock && clock.visit_id === v.id;
        return (
          <Modal title={`${v.jobNumber ? `#${v.jobNumber} — ` : ""}${v.title || v.jobTitle || "Visit"}`} onClose={() => setDetail(null)}>
            <div style={{ fontSize: 13.5, color: C.muted, marginBottom: 10, lineHeight: 1.6 }}>
              {new Date(v.startAt).toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" })}
              {" · "}{v.allDay ? "All day" : `${fmtTime(v.startAt)}${v.endAt ? ` – ${fmtTime(v.endAt)}` : ""}`}
              {meta.clientName && <><br />Client: <span style={{ color: C.text }}>{meta.clientName}</span></>}
              <br />Techs: <span style={{ color: C.text }}>{v.assigned.join(", ") || "Unassigned"}</span>
              {v.complete && <span style={{ color: C.green, fontWeight: 700 }}> · Done ✓</span>}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
              {!clockedHere && !v.complete && <Btn kind="pri" onClick={() => { setDetail(null); doClockIn(v); }} disabled={clockBusy}>⏱ Clock in{v.jobNumber ? ` on #${v.jobNumber}` : ""}</Btn>}
              {clockedHere && <Btn kind="danger" onClick={() => { setDetail(null); doClockOut(); }} disabled={clockBusy}>⏱ Clock out</Btn>}
              {canEdit && <Btn onClick={() => setDialog({ type: "reschedule", kind: "visit", item: v })}>🕑 Reschedule</Btn>}
              {canEdit && <Btn onClick={() => setDialog({ type: "assign", item: v })}>👥 Assign techs</Btn>}
              {canOpenJob && v.jobNumber && (
                <Link to={`/job/${v.jobNumber}`} style={{ textDecoration: "none" }}><Btn>Open full job →</Btn></Link>
              )}
              {v.jobNumber && (
                <Link to={`/panel-schedule?job=${v.jobNumber}&visit=${encodeURIComponent(v.id)}`} style={{ textDecoration: "none" }}>
                  <Btn title="Circuit directory for this job's panel — write circuits, print the door label">🔲 Panel schedule</Btn>
                </Link>
              )}
              {v.jobNumber && genByJob[String(v.jobNumber)] && (
                <Link to={`/generator-report?job=${v.jobNumber}&visit=${encodeURIComponent(v.id)}`} style={{ textDecoration: "none" }}>
                  <Btn title={genByJob[String(v.jobNumber)].units?.[0]?.label || "Standby generator annual maintenance report"}>
                    ⚡ Generator report{genByJob[String(v.jobNumber)].units?.[0]?.label ? ` — ${genByJob[String(v.jobNumber)].units[0].label}` : ""}
                  </Btn>
                </Link>
              )}
            </div>
            {v.jobNumber && (notesByJob[v.jobNumber] || []).length > 0 && (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                {(notesByJob[v.jobNumber] || []).map((n) => (
                  <div key={n.id} style={{ background: C.card, borderRadius: 8, padding: "8px 10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11.5, color: C.faint }}>
                        {new Date(n.created_at).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        {Array.isArray(n.photo_paths) && n.photo_paths.length ? ` · ${n.photo_paths.length} 📷` : ""}
                      </span>
                      <StatusChip status={n.status} />
                    </div>
                    <div style={{ fontSize: 13.5, marginTop: 4, whiteSpace: "pre-wrap" }}>{n.message}</div>
                  </div>
                ))}
              </div>
            )}
            {v.jobNumber && (
              <NoteComposer visit={v} clientName={meta.clientName} jobberJobId={meta.jobberJobId} onQueued={() => loadNotes(v.jobNumber)} />
            )}
          </Modal>
        );
      })()}

      {detail && detail.kind === "task" && (() => {
        const t = detail.item;
        return (
          <Modal title={`📋 ${t.title || "Task"}`} onClose={() => setDetail(null)}>
            <div style={{ fontSize: 13.5, color: C.muted, marginBottom: 10, lineHeight: 1.6 }}>
              {t.startAt && new Date(t.startAt).toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" })}
              {" · "}{t.allDay ? "All day" : `${fmtTime(t.startAt)}${t.endAt ? ` – ${fmtTime(t.endAt)}` : ""}`}
              {t.clientName && <><br />Client: <span style={{ color: C.text }}>{t.clientName}</span></>}
              <br />Assigned: <span style={{ color: C.text }}>{t.assigned.join(", ") || "Unassigned"}</span>
              {t.complete && <span style={{ color: C.green, fontWeight: 700 }}> · Done ✓</span>}
            </div>
            {t.instructions && (
              <div style={{ background: C.card, borderRadius: 8, padding: "10px 12px", fontSize: 13.5, whiteSpace: "pre-wrap", marginBottom: 10 }}>
                {t.instructions}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(canEdit || t.mine) && (
                <Btn kind={t.complete ? undefined : "pri"} onClick={async () => {
                  setBusyMsg(t.complete ? "Un-marking…" : "Marking done…");
                  const r = await api("task-toggle-complete", { taskId: t.id });
                  setBusyMsg(null);
                  if (r.ok) { setDetail(null); load(); } else alert(r.error || "Couldn't update");
                }}>{t.complete ? "↩ Not done after all" : "✓ Mark done"}</Btn>
              )}
              {canEdit && <Btn onClick={() => setDialog({ type: "task-edit", item: t })}>✏️ Edit</Btn>}
              {canEdit && <Btn onClick={() => setDialog({ type: "reschedule", kind: "task", item: t })}>🕑 Reschedule</Btn>}
              {canEdit && (
                <Btn kind="danger" onClick={async () => {
                  if (!window.confirm("Delete this task? It comes off the Jobber calendar too.")) return;
                  setBusyMsg("Deleting…");
                  const r = await api("task-delete", { taskId: t.id });
                  setBusyMsg(null);
                  if (r.ok) { setDetail(null); load(); } else alert(r.error || "Couldn't delete");
                }}>🗑 Delete</Btn>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: C.faint, marginTop: 8 }}>
              "Done" renames the task with a ✓ in Jobber — same convention as "✓ Quote sent" (Jobber's API can't tick its real checkbox).
            </div>
          </Modal>
        );
      })()}

      {/* ══ DIALOGS ══ */}
      {dialog?.type === "task-new" && (
        <TaskDialog crew={crew} initial={{ date: dateInputVal(anchor) }} onClose={() => setDialog(null)}
          onSave={async (payload) => { const r = await api("task-create", payload); if (r.ok) load(); return r; }} />
      )}
      {dialog?.type === "task-edit" && (
        <TaskDialog crew={crew} initial={dialog.item} onClose={() => setDialog(null)}
          onSave={async (payload) => { const r = await api("task-edit", payload); if (r.ok) { setDetail(null); load(); } return r; }} />
      )}
      {dialog?.type === "visit-new" && (
        <BookVisitDialog crew={crew} defaultDate={anchor} onClose={() => setDialog(null)}
          onSave={async (payload) => { const r = await api("visit-create", payload); if (r.ok) load(); return r; }} />
      )}
      {dialog?.type === "reschedule" && (
        <RescheduleDialog item={dialog.item} onClose={() => setDialog(null)}
          onSave={(startAt, endAt) => doMove(dialog.kind, dialog.item, startAt, endAt)} />
      )}
      {dialog?.type === "assign" && (() => {
        const v = dialog.item;
        return (
          <AssignDialog crew={crew} item={v} onClose={() => setDialog(null)}
            onSave={async (ids) => {
              const r = await api("visit-assign", { visitId: v.id, assignedUserIds: ids });
              if (r.ok) { setDetail(null); load(); }
              return r;
            }} />
        );
      })()}

      {/* Drag-move confirm */}
      {moveConfirm && (
        <Modal title="Reschedule" onClose={() => setMoveConfirm(null)}>
          <div style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
            Move <b>{moveConfirm.item.title || (moveConfirm.item.jobNumber ? `Job #${moveConfirm.item.jobNumber}` : "this")}</b>
            {moveConfirm.item.assigned.length ? <> ({moveConfirm.item.assigned.join(", ")})</> : null} to<br />
            <b style={{ color: C.lime }}>{moveConfirm.dayLabel} at {fmtTime(moveConfirm.newStart.toISOString())}</b>?
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 8 }}>This updates Jobber.</div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn onClick={() => setMoveConfirm(null)} disabled={!!busyMsg}>Cancel</Btn>
            <Btn kind="pri" disabled={!!busyMsg} onClick={async () => {
              const mc = moveConfirm;
              const r = await doMove(mc.kind, mc.item, mc.newStart.toISOString(), mc.newEnd ? mc.newEnd.toISOString() : null);
              setMoveConfirm(null);
              if (!r.ok) alert(r.error || "Reschedule failed");
            }}>{busyMsg ? "Moving…" : "Reschedule"}</Btn>
          </div>
        </Modal>
      )}

      {busyMsg && !moveConfirm && (
        <div style={{ position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)", background: C.card, border: `1px solid ${C.border}`, borderRadius: 999, padding: "8px 18px", fontSize: 13, zIndex: 400 }}>
          {busyMsg}
        </div>
      )}
    </div>
  );
}

// ── Assign-techs dialog ─────────────────────────────────────────────────────
function AssignDialog({ crew, item, onSave, onClose }) {
  const [ids, setIds] = useState(item.assignedIds || []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  return (
    <Modal title={`Assign techs${item.jobNumber ? ` — #${item.jobNumber}` : ""}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <CrewPicker crew={crew} value={ids} onChange={setIds} />
        {err && <div style={{ color: C.red, fontSize: 13 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn kind="pri" disabled={busy} onClick={async () => {
            setBusy(true); setErr(null);
            const r = await onSave(ids);
            setBusy(false);
            if (!r.ok) setErr(r.error || "Couldn't update"); else onClose();
          }}>{busy ? "Saving…" : "Save — updates Jobber"}</Btn>
        </div>
      </div>
    </Modal>
  );
}
